import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  FormGroup,
} from '@angular/forms';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

import { InventoryService } from '../../../services/inventory.service';
import { NotificationService } from '../../../services/notification.service';
import { parseCategoryKind } from '../../../utils/inventory-category-kind';

@Component({
  selector: 'app-inventory-category-dialog',
  standalone: true,
  providers: [ConfirmationService],
  imports: [
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ConfirmDialogModule,
  ],
  templateUrl: './inventory-category-dialog.component.html',
  styleUrl: './inventory-category-dialog.component.scss',
})
export class InventoryCategoryDialogComponent implements OnInit {
  categories: any[] = [];
  loading = false;
  changed = false;

  form!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private inventoryService: InventoryService,
    private notification: NotificationService,
    private confirmationService: ConfirmationService,
    private ref: DynamicDialogRef,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
    });
    this.load();
  }

  load(): void {
    this.loading = true;

    this.inventoryService.getCategories().subscribe({
      next: (cats) => {
        this.categories = (cats ?? []).map((c: any) => ({
          ...c,
          kind: parseCategoryKind(c.kind),
        }));
        this.loading = false;
      },
      error: (err) => {
        this.categories = [];
        this.loading = false;
        this.notification.error(
          'Die Kategorien konnten nicht geladen werden.',
          err,
        );
      },
    });
  }

  // Die Art wird im Backend aus dem Namen abgeleitet und steuert, welche Felder
  // ein Eintrag dieser Kategorie bekommt.
  kindLabel(kind: string): string {
    return kind === 'BOOK' ? 'Buch' : 'Technik';
  }

  add(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const name = (this.form.value.name ?? '').toString().trim();
    if (!name) return;

    this.loading = true;

    this.inventoryService.createCategory(name).subscribe({
      next: () => {
        this.changed = true;
        this.form.reset({ name: '' });
        this.load();
      },
      error: (err) => {
        this.loading = false;
        this.notification.error(
          err?.error?.message ?? 'Die Kategorie konnte nicht angelegt werden.',
          err,
        );
      },
    });
  }

  remove(cat: any): void {
    const id = Number(cat?.id ?? cat?.categoryId);
    const name = (cat?.name ?? '').toString();

    if (!id) return;

    this.confirmationService.confirm({
      message: `Kategorie "${name}" wirklich löschen?`,
      header: 'Löschbestätigung',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteCategory(id),
    });
  }

  private deleteCategory(id: number): void {
    this.loading = true;

    this.inventoryService.deleteCategory(id).subscribe({
      next: () => {
        this.changed = true;
        this.load();
      },
      error: (err) => {
        this.loading = false;
        this.notification.error(
          err?.error?.message ?? 'Die Kategorie konnte nicht gelöscht werden.',
          err,
        );
      },
    });
  }

  close(): void {
    this.ref.close({ changed: this.changed });
  }
}
